<template>
    <div class="flex-fill" v-if="isDesktop" layout>
        <HeaderComp :creator="creator" />
        <div class="d-flex">
            <NavComp :creator="creator" />
            <LeftPanelComp :layers="layers" />
            <CenterComp :creator="creator" ref='center' />
            <InspectorComp :selected="selected" />
        </div>
        <FooterComp :axis="axis" />
        <div v-if="reminders.length" class="reminder-stack">
            <div
                v-for="reminder in reminders"
                :key="reminder.id"
                class="reminder-toast"
            >
                {{ reminder.message }}
            </div>
        </div>
        <ObjectContextMenuComp :creator="creator" :object_uid="contextmenu.object.uid" v-if="contextmenu.object.enabled" :menu_position="contextmenu.position" />
        <LayerContextMenuComp :creator="creator" :object_uid="contextmenu.layer.uid" v-if="contextmenu.layer.enabled" :menu_position="contextmenu.position" />
        <ExportComp :canvas="{ width: 100, height: 100 }" ref="export" />
        <!-- <ObjectUiInfoComp v-if="selected" :object="selected" /> -->
    </div>
    <div v-else class="mobile-block">
        <p>This editor is currently available on desktop devices only.</p>
    </div>
</template>

<script>
import { Creator } from '@/lib/visionary/visionary';

// Section Components
import HeaderComp from '@/components/designer/HeaderComp.vue';
import NavComp from '@/components/designer/NavComp.vue';
import LeftPanelComp from '@/components/designer/LeftPanelComp.vue';
import CenterComp from '@/components/designer/CenterComp.vue';
import InspectorComp from '@/components/designer/InspectorComp.vue';
import FooterComp from '@/components/designer/FooterComp.vue';
// Context Menu Components
import ObjectContextMenuComp from '@/components/designer/contextmenus/ObjectContextMenuComp.vue';
import LayerContextMenuComp from '@/components/designer/contextmenus/LayerContextMenuComp.vue';
import ExportComp from '@/components/designer/ExportComp.vue';

import { VectorObject } from '@/lib/visionary/objects/vector';
import { ImageObject } from '@/lib/visionary/objects/image';

export default {
    name: 'MainView',
    data() {
        return {
            creator: null,
            layers: [],
            object: null,
            selected: null,
            reminders: [],
            reminderTimers: {},
            contextmenu: {
                layer: {
                    uid: null,
                    enabled: false,
                    position: {
                        x: 0,
                        y: 0
                    }
                },
                object: {
                    uid: null,
                    enabled: false,
                    position: {
                        x: 0,
                        y: 0
                    }
                }
            },
        }
    },
    props: {
        msg: String
    },
    components: {
        NavComp,
        HeaderComp,
        LeftPanelComp,
        CenterComp,
        InspectorComp,
        FooterComp,
        ObjectContextMenuComp,
        LayerContextMenuComp,
        ExportComp
    },
    mounted() {
        // mount the canvas in this moment
        const canvas = this.$refs.center.$refs.canvas;
        const export_canvas = this.$refs.export.$refs.canvas;
    
        this.creator = new Creator({
            canvasElement: canvas,
            exportElement: export_canvas,
            saveState: this.$store.state.editor.saveState
        });
    
        // this.activeTool = getActiveTool();
        // console.log(this.activeTool);

        this.layers = this.creator.objects;
    
        canvas.addEventListener('creator:position', (event) => {
            this.axis.x = event.detail.x;
            this.axis.y = event.detail.y;

            this.save(this.creator);
        });
        canvas.addEventListener('creator:object:create', (event) => {
            this.layers = [...this.creator.objects, event.detail.object];

            this.save(this.creator);
        });
        canvas.addEventListener('creator:object:selected', (event) => {
            this.selected = {
                context: event.detail.object.context,
                uid: event.detail.object.uid,
                position: {
                    x: event.detail.object.position.x,
                    y: event.detail.object.position.y
                },
                dimensions: {
                    width: event.detail.object.dimensions.width,
                    height: event.detail.object.dimensions.height
                }
            }
            if( event.detail.object instanceof VectorObject ) {
                this.selected = {
                    ...this.selected,
                    fill: {
                        enabled: event.detail.object.fill.enabled,
                        color: event.detail.object.fill.color
                    },
                    stroke: {
                        enabled: event.detail.object.stroke.enabled,
                        color: event.detail.object.stroke.color,
                        width: event.detail.object.stroke.width
                    },
                    effects: {
                        enabled: event.detail.object.effects.enabled,
                        shadow: event.detail.object.effects.shadow
                    }
                }
            }
            if( event.detail.object instanceof ImageObject ) {
                const object = event.detail.object;
                
                this.selected = {
                    ...this.selected,
                    fill: { ...object.fill }
                }
            }

            this.selected.context.canvas.dispatchEvent(new CustomEvent('creator:inspector:update', {detail: this.selected}));
            this.creator.refresh();

            this.save(this.creator);
        });
        canvas.addEventListener('creator:object:update', (event) => {
            // This is for base object
            this.selected = {
                context: event.detail.object.context,
                uid: event.detail.object.uid,
                position: {
                    x: event.detail.object.position.x,
                    y: event.detail.object.position.y
                },
                dimensions: {
                    width: event.detail.object.dimensions.width,
                    height: event.detail.object.dimensions.height
                }
            }

            // This is for specific objects
            if( event.detail.object instanceof VectorObject ) {
                this.selected = {
                    ...this.selected,
                    fill: {
                        enabled: event.detail.object.fill.enabled,
                        color: event.detail.object.fill.color
                    },
                    stroke: {
                        enabled: event.detail.object.stroke.enabled,
                        color: event.detail.object.stroke.color,
                        width: event.detail.object.stroke.width
                    },
                    effects: {
                        enabled: event.detail.object.effects.enabled,
                        shadow: event.detail.object.effects.shadow
                    }
                }
            }
            
            this.selected.context.canvas.dispatchEvent(new CustomEvent('creator:inspector:update', {detail: this.selected}));
            this.creator.refresh();
            
            this.save(this.creator);
        });
        canvas.addEventListener('creator:object:delete', (event) => {
            // code here ...
            this.layers = this.creator.objects;
        });
        canvas.addEventListener('creator:object:contextmenu', (event) => {
            const state = event.detail.open;
            
            if(event.detail.object) {
                if( state ) this.contextmenu.object.uid = event.detail.object.uid;
            }

            this.contextmenu.object.enabled = state;
            this.contextmenu.position = {
                x: event.detail.position.x,
                y: event.detail.position.y
            }
            this.creator.refresh();
            this.save(this.creator);
        });
        canvas.addEventListener('creator:tool:selected', (event) => {
            const selected_tool = event.detail.selected_tool;

            this.save(this.creator);
        });
    
        //     // this.addReminder("Reminder: there is a coordinate issue with rendering vectors on the y axis");
        //     // this.addReminder("Reminder: make the object delete-able");
        //     // this.addReminder("Reminder: allow removal of vertex curve");
        //     // this.addReminder("Reminder: fix the distance for control points");
    },
    methods: {
        addReminder(message, duration = 5000) {
            const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
            this.reminders.push({ id, message });

            const timer = setTimeout(() => {
                this.removeReminder(id);
            }, duration);

            this.reminderTimers[id] = timer;
        },
        removeReminder(id) {
            const index = this.reminders.findIndex((reminder) => reminder.id === id);
            if (index !== -1) {
                this.reminders.splice(index, 1);
            }
            if (this.reminderTimers[id]) {
                clearTimeout(this.reminderTimers[id]);
                delete this.reminderTimers[id];
            }
        },
        clearAllReminders() {
            Object.values(this.reminderTimers).forEach((timer) => clearTimeout(timer));
            this.reminders = [];
            this.reminderTimers = {};
        },
        save(creator) {
            const state = creator.getState();

            // console.log(this.$store._actions);
            this.$store.dispatch('editor/saveCreatorState', {state});
        }
    },
    computed: {
        axis() {
            const saveState = this.$store.state.editor.saveState;
            const axis = saveState ? saveState.position : { x: 0, y: 0 };
            return { ...axis };
        },
        isDesktop() {
            if (typeof navigator === 'undefined') return true;
            const ua = (navigator.userAgent || navigator.vendor || window.opera || '').toLowerCase();
            return !/android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua);
        }
    },
    beforeUnmount() {
        this.clearAllReminders();
    },
    beforeDestroy() {
        this.clearAllReminders();
    }
}
</script>

<style>
body {
    overflow: hidden;
}

div[layout] {
    display: flex;
    flex-direction: column;
    height: 100vh;
    display: grid;
    grid-template-rows: 46px 1fr 26px;
    background: var(--bg);
    color: var(--text);
    font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji";
}
.main { display: grid; grid-template-columns: 56px 240px 1fr 320px; min-height: 0; }

.reminder-stack {
    position: fixed;
    bottom: 32px;
    right: 24px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.reminder-toast {
    background: var(--panel);
    color: var(--text);
    padding: 12px 16px;
    border-radius: 8px;
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.25);
    pointer-events: none;
    font-size: 14px;
    line-height: 1.3;
}

.mobile-block {
    min-height: 100vh;
    display: grid;
    place-items: center;
    padding: 24px;
    background: var(--bg);
    color: var(--muted);
    font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji";
    text-align: center;
}
</style>
